import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface LeadNotification {
  id: string;
  leadId: string;
  leadValue: string;
  leadType: 'email' | 'phone';
  conversationId: string;
  chatbotId: string | null;
  chatbotName: string;
  createdAt: string;
  read: boolean;
  firstMessage: string | null;
}

export interface NotificationPreference {
  chatbotId: string;
  enabled: boolean;
}

interface NotificationState {
  notifications: LeadNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  preferences: Record<string, boolean>; // chatbotId -> enabled
  
  // Actions
  loadNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAllAsReadForChatbot: (chatbotId: string) => Promise<void>;
  clearNotifications: () => Promise<void>;
  setupNotificationListener: () => () => void; // Returns cleanup function
  toggleNotificationsForChatbot: (chatbotId: string, enabled: boolean) => Promise<void>;
  loadNotificationPreferences: () => Promise<void>;
}

// Storage key for preferences
const PREFERENCES_STORAGE_KEY = 'chatbot_notification_preferences';

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  preferences: {},
  
  loadNotifications: async () => {
    try {
      set({ loading: true, error: null });
      
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Fetch lead notifications from the database
      // Join with chatbots table to filter only notifications for chatbots owned by the current user
      const { data, error } = await supabase
        .from('conversation_contact_info')
        .select(`
          id,
          type,
          value,
          conversation_id,
          chatbot_public_id,
          created_at,
          processed,
          conversations(initial_message_content),
          chatbots!inner(name, user_id)
        `)
        .eq('chatbots.user_id', user.id) // Only select notifications for chatbots owned by this user
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      // Format the notifications
      const notifications: LeadNotification[] = data.map(item => ({
        id: item.id,
        leadId: item.id,
        leadValue: item.value,
        leadType: item.type as 'email' | 'phone',
        conversationId: item.conversation_id,
        chatbotId: item.chatbot_public_id,
        chatbotName: item.chatbots?.name || 'Unknown chatbot',
        createdAt: item.created_at,
        read: item.processed || false,
        firstMessage: item.conversations?.initial_message_content || null,
      }));
      
      // Get preferences
      const preferences = get().preferences;
      
      // Filter out notifications for chatbots where notifications are disabled
      const filteredNotifications = notifications.filter(
        n => n.chatbotId === null || preferences[n.chatbotId] !== false
      );
      
      // Count unread notifications
      const unreadCount = filteredNotifications.filter(n => !n.read).length;
      
      set({
        notifications: filteredNotifications,
        unreadCount,
        loading: false,
      });
    } catch (err) {
      console.error('Error loading notifications:', err);
      set({ error: err instanceof Error ? err.message : 'Unknown error', loading: false });
    }
  },
  
  markAsRead: async (id: string) => {
    try {
      // First update locally to make UI responsive
      set(state => {
        const notifications = state.notifications.map(notification => 
          notification.id === id ? { ...notification, read: true } : notification
        );
        const unreadCount = notifications.filter(n => !n.read).length;
        return { notifications, unreadCount };
      });
      
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Then update in the database - using 'processed' column
      // Only update for chatbots owned by this user
      const { error } = await supabase
        .from('conversation_contact_info')
        .update({ processed: true })
        .eq('id', id)
        .filter('chatbot_public_id', 'in', `(select public_id from chatbots where user_id = '${user.id}')`);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      
      // Reload notifications in case of error
      get().loadNotifications();
    }
  },
  
  markAllAsRead: async () => {
    try {
      // First update locally to make UI responsive
      set(state => ({
        notifications: state.notifications.map(notification => ({ ...notification, read: true })),
        unreadCount: 0
      }));
      
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Then update all in the database - using 'processed' column
      // Only update for chatbots owned by this user
      const { error } = await supabase
        .from('conversation_contact_info')
        .update({ processed: true })
        .eq('processed', false)
        .filter('chatbot_public_id', 'in', `(select public_id from chatbots where user_id = '${user.id}')`);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      
      // Reload notifications in case of error
      get().loadNotifications();
    }
  },
  
  markAllAsReadForChatbot: async (chatbotId: string) => {
    try {
      if (!chatbotId) return;
      
      // First update locally to make UI responsive
      set(state => {
        const notifications = state.notifications.map(notification => 
          notification.chatbotId === chatbotId ? { ...notification, read: true } : notification
        );
        const unreadCount = notifications.filter(n => !n.read).length;
        return { notifications, unreadCount };
      });
      
      // Get the current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Then update in the database
      // Only update for chatbots owned by this user
      const { error } = await supabase
        .from('conversation_contact_info')
        .update({ processed: true })
        .eq('chatbot_public_id', chatbotId)
        .eq('processed', false)
        .filter('chatbot_public_id', 'in', `(select public_id from chatbots where user_id = '${user.id}')`);
      
      if (error) throw error;
    } catch (err) {
      console.error('Error marking chatbot notifications as read:', err);
      
      // Reload notifications in case of error
      get().loadNotifications();
    }
  },
  
  clearNotifications: async () => {
    // We don't actually delete notifications, just mark them as read
    return get().markAllAsRead();
  },
  
  loadNotificationPreferences: async () => {
    try {
      // Get from localStorage
      const storedPrefs = localStorage.getItem(PREFERENCES_STORAGE_KEY);
      
      if (storedPrefs) {
        try {
          const preferences = JSON.parse(storedPrefs);
          set({ preferences });
        } catch (e) {
          console.error('Error parsing stored notification preferences', e);
          localStorage.removeItem(PREFERENCES_STORAGE_KEY);
        }
      }
      
      // Reload notifications to apply the preferences
      await get().loadNotifications();
    } catch (err) {
      console.error('Error loading notification preferences:', err);
    }
  },
  
  toggleNotificationsForChatbot: async (chatbotId: string, enabled: boolean) => {
    try {
      if (!chatbotId) return;
      
      // Update preferences locally
      const newPreferences = {
        ...get().preferences,
        [chatbotId]: enabled
      };
      
      // Save to state
      set({ preferences: newPreferences });
      
      // Save to localStorage for persistence
      localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(newPreferences));
      
      // Reload notifications to apply the new preference
      await get().loadNotifications();
    } catch (err) {
      console.error('Error toggling notifications for chatbot:', err);
    }
  },
  
  setupNotificationListener: () => {
    // Load notifications and preferences
    get().loadNotifications();
    get().loadNotificationPreferences();
    
    // Get the current user ID
    const getCurrentUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.id;
    };
    
    // Set up realtime subscription
    let subscription: any = null;
    
    getCurrentUserId().then(userId => {
      if (!userId) {
        console.error('Cannot setup notification listener: User not authenticated');
        return;
      }
      
      // Subscribe to realtime changes
      // We get notified of all new notifications, but we'll filter in loadNotifications
      subscription = supabase
        .channel('lead-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'conversation_contact_info',
          },
          (payload) => {
            console.log('New lead notification received:', payload);
            // Reload notifications when new leads are detected
            // loadNotifications will filter by user
            get().loadNotifications();
          }
        )
        .subscribe((status) => {
          console.log('Supabase realtime subscription status:', status);
        });
    });
    
    // Return cleanup function
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  },
})); 